import { useServiceNavigation } from "@/features/authorization/hooks/useServiceNavigation";
import { canOpenPage } from "@/features/authorization/pageAccess";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useEffect, useState } from "react";
import { Command } from "cmdk";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  CalendarDays,
  Home,
  LayoutDashboard,
  Search,
  Users,
} from "lucide-react";

type CommandItem = {
  label: string;
  description: string;
  to: string;
  icon: React.ReactNode;
};

const commands: CommandItem[] = [
  {
    label: "Overview",
    description: "Open operations overview",
    to: "/",
    icon: <LayoutDashboard size={18} />,
  },
  {
    label: "Members",
    description: "Manage members and engagement",
    to: "/members",
    icon: <Users size={18} />,
  },
  {
    label: "Households",
    description: "Manage households and family care",
    to: "/households",
    icon: <Home size={18} />,
  },
  {
    label: "Attendance",
    description: "Record and review attendance",
    to: "/attendance",
    icon: <CalendarDays size={18} />,
  },
  {
    label: "Services",
    description: "Manage services and gatherings",
    to: "/services",
    icon: <CalendarDays size={18} />,
  },
  {
    label: "Care/Follow-up",
    description: "Review and manage ministry care follow-up",
    to: "/care-queue",
    icon: <Activity size={18} />,
  },
];

export function CommandPalette() {
  const { permissions } = useEffectivePermissions();
  const servicesPath = useServiceNavigation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }

      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  function runCommand(to: string) {
    setOpen(false);
    navigate(to);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 px-4 pt-28 backdrop-blur-sm">
      <div
        className="absolute inset-0"
        onClick={() => setOpen(false)}
      />

      <Command className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-black/50">
        <div className="flex items-center gap-3 border-b border-slate-800 px-5 py-4">
          <Search className="h-5 w-5 text-slate-500" />

          <Command.Input
            autoFocus
            placeholder="Search MinistryIQ..."
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-slate-500"
          />

          <kbd className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-500">
            ESC
          </kbd>
        </div>

        <Command.List className="max-h-96 overflow-y-auto p-3">
          <Command.Empty className="px-4 py-10 text-center text-sm text-slate-500">
            No command found.
          </Command.Empty>

          <Command.Group heading="Navigate">
            {commands.filter((item) => item.to === "/services" ? !!servicesPath : canOpenPage(item.to, permissions)).map((item) => (
              <Command.Item
                key={item.to}
                value={`${item.label} ${item.description}`}
                onSelect={() => runCommand(item.to === "/services" ? servicesPath! : item.to)}
                className="flex cursor-pointer items-center gap-4 rounded-xl px-4 py-3 text-slate-300 aria-selected:bg-indigo-500/15 aria-selected:text-white"
              >
                <div className="rounded-lg bg-slate-900 p-2 text-indigo-400">
                  {item.icon}
                </div>

                <div>
                  <p className="font-medium">{item.label}</p>
                  <p className="text-xs text-slate-500">
                    {item.description}
                  </p>
                </div>
              </Command.Item>
            ))}
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  );
}

